import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { db } from "../../config/firebase";
import { WELCOME_COLLECTION, WELCOME_DOC_ID } from "../../utils/instructorWelcome";

// Editor for the welcome email that the envelope button on each instructor
// sends. Plain text only: line breaks are kept, and the email API encodes
// anything that looks like HTML so it can never render.
const InstructorWelcomeEmailEditor = () => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const loadTemplate = async () => {
      try {
        const snap = await getDoc(doc(db, WELCOME_COLLECTION, WELCOME_DOC_ID));
        if (snap.exists()) {
          setSubject(snap.data().subject || "");
          setBody(snap.data().body || "");
        }
      } catch (error) {
        console.error("Error loading welcome email:", error);
        setSnackbar({
          open: true,
          message: "Could not load the saved welcome email.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    loadTemplate();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(
        doc(db, WELCOME_COLLECTION, WELCOME_DOC_ID),
        { subject: subject.trim(), body, updated_at: serverTimestamp() },
        { merge: true },
      );
      setSnackbar({
        open: true,
        message: "Welcome email saved.",
        severity: "success",
      });
    } catch (error) {
      console.error("Error saving welcome email:", error);
      setSnackbar({
        open: true,
        message: `Could not save the welcome email: ${error.message}`,
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 6 }}>
      <Typography variant="h6" gutterBottom>
        Welcome Email
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This is the email sent when you click the envelope on an instructor.
        Plain text only — line breaks are kept.
      </Typography>

      <TextField
        label="Subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        fullWidth
        margin="dense"
        inputProps={{ maxLength: 200 }}
      />
      <TextField
        label="Email Body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        fullWidth
        margin="dense"
        multiline
        minRows={12}
        inputProps={{ maxLength: 20000 }}
      />

      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !body.trim()}
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
        >
          Save
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default InstructorWelcomeEmailEditor;
